import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { UserRolePermissions, AccessLevel } from '@/types/permissions';
import { useTimesheet } from './TimesheetContext';
import { useDataSource } from './DataSourceContext';
import { apiClient } from '@/services/apiClient';

// Storage key for employee role assignments in demo mode
const EMPLOYEE_ROLE_ASSIGNMENT_KEY = 'employeeRoleAssignments';

// Helper to get employee's assigned role ID from localStorage (demo mode)
function getEmployeeRoleIdFromStorage(employeeId: number): number | null {
  try {
    const stored = localStorage.getItem(EMPLOYEE_ROLE_ASSIGNMENT_KEY);
    if (stored) {
      const assignments = JSON.parse(stored) as Record<string, number>;
      return assignments[String(employeeId)] ?? null;
    }
  } catch (error) {
    console.error('[PermissionContext] Error loading employee role assignment:', error);
  }
  return null;
}

// Helper to load role permissions from localStorage (demo mode)
function loadRolePermissionsFromStorage(roleId: number): UserRolePermissions | null {
  try {
    const permissionsKey = `userRolePermissions_${roleId}`;
    const stored = localStorage.getItem(permissionsKey);
    if (stored) {
      return JSON.parse(stored) as UserRolePermissions;
    }
  } catch (error) {
    console.error('[PermissionContext] Error loading role permissions:', error);
  }
  return null;
}

// Helper to get role name from localStorage (demo mode)
function getRoleNameFromStorage(roleId: number): string | null {
  try {
    const stored = localStorage.getItem('userRoles');
    if (stored) {
      const roles = JSON.parse(stored) as Array<{ id: number; name: string }>;
      const role = roles.find(r => r.id === roleId);
      return role?.name ?? null;
    }
  } catch (error) {
    console.error('[PermissionContext] Error loading role name:', error);
  }
  return null;
}

interface PermissionContextType {
  permissions: UserRolePermissions | null;
  userRoleId: number | null;
  userRoleName: string | null;
  isLoading: boolean;
  
  // Timesheet permissions
  canViewTimesheet: (targetEmployeeId: number) => boolean;
  canEditTimesheet: (targetEmployeeId: number) => boolean;
  canDeleteTimesheet: () => boolean;
  canDuplicateTimesheet: () => boolean;
  canRecallTimesheet: () => boolean;
  getTimesheetViewAccessLevel: () => AccessLevel;
  getTimesheetEditAccessLevel: () => AccessLevel;
  
  // Personal homepage permission
  canViewPersonalHomepage: () => boolean;

  // Approval permissions
  canViewApprovalDashboard: () => boolean;
  canViewApprovals: () => boolean;
  canApproveReject: () => boolean;
  canEditTimesheetInApproval: () => boolean;
  canBulkApprove: () => boolean;
  canDelegateApproval: () => boolean;
  requiresRejectionReason: () => boolean;
  canViewPolicyViolations: () => boolean;
  canViewDetectedIssues: () => boolean;
  
  // Report permissions
  canViewReport: (reportType: 'timesheet' | 'utilisation' | 'missingTimesheets') => boolean;
  canExportReport: (reportType: 'timesheet' | 'utilisation' | 'missingTimesheets') => boolean;
  getReportViewAccessLevel: (reportType: 'timesheet' | 'utilisation' | 'missingTimesheets') => AccessLevel;
  
  // System settings permissions
  canManageRolesPermissions: () => boolean;
  canManageSystemSettings: () => boolean;
  canViewAuditLogs: () => boolean;
  canManageApprovalWorkflows: () => boolean;
  canManageNotifications: () => boolean;
  canAccessAnySettings: () => boolean;
  // Settings tab sub-permissions
  canManageDeadlines: () => boolean;
  canManagePolicySettings: () => boolean;
  canManageUtilisationSettings: () => boolean;
  canManageTimeEntrySettings: () => boolean;
  canManageWorkTypeSettings: () => boolean;
  
  // Team/Employee directory permissions
  canViewEmployeeDirectory: () => boolean;
  canViewEmployeeDetails: () => boolean;
  
  // Integrations permissions
  canViewIntegrations: () => boolean;
  canManageIntegrations: () => boolean;
  
  // Company management permissions
  canViewCompanies: () => boolean;
  
  // Any reports permission
  canAccessAnyReports: () => boolean;
  
  // Smart features permissions (legacy — kept for backward compatibility)
  canUseAiAssistant: () => boolean;
  canUseAnomalyDetection: () => boolean;

  // Smart features permissions (correctly named)
  canCopyFromLastWeek: () => boolean;
  canUseSmartFill: () => boolean;
  canUseAiInsights: () => boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

// Interface for the API response when fetching current employee
interface CurrentEmployeeResponse {
  id: number;
  name: string;
  email: string;
  userRoleId?: number;
  userRoleName?: string;
  permissions?: UserRolePermissions;
}

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentEmployee } = useTimesheet();
  const { getScreenMode } = useDataSource();
  const [permissions, setPermissions] = useState<UserRolePermissions | null>(null);
  const [userRoleId, setUserRoleId] = useState<number | null>(null);
  const [userRoleName, setUserRoleName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Track if we've already fetched permissions from API to avoid duplicate calls
  const hasFetchedFromApi = useRef(false);

  // Function to fetch permissions from API (for live mode)
  const fetchPermissionsFromApi = useCallback(async () => {
    try {
      const response = await apiClient.get<CurrentEmployeeResponse>('/employees/me');
      
      if (response.permissions) {
        setPermissions(response.permissions);
        setUserRoleId(response.userRoleId ?? null);
        setUserRoleName(response.userRoleName ?? null);
      } else {
        setPermissions(getDefaultPermissions());
        setUserRoleId(response.userRoleId ?? null);
        setUserRoleName(response.userRoleName ?? null);
      }
      hasFetchedFromApi.current = true;
    } catch (error) {
      console.error('[PermissionContext] Error fetching permissions from API:', error);
      // Fall back to defaults on error
      setPermissions(getDefaultPermissions());
      setUserRoleId(null);
      setUserRoleName(null);
    }
  }, []);

  // Function to reload permissions from localStorage (for demo mode)
  const reloadPermissionsFromStorage = useCallback(() => {
    if (!currentEmployee) return;
    
    const mode = getScreenMode('timesheet-grid');
    if (mode !== 'demo') return;
    
    // Check for currently editing role first
    const currentEditingRoleId = localStorage.getItem('currentEditingRoleId');
    if (currentEditingRoleId) {
      const roleId = parseInt(currentEditingRoleId, 10);
      const storedPermissions = loadRolePermissionsFromStorage(roleId);
      const roleName = getRoleNameFromStorage(roleId);
      if (storedPermissions) {
        setPermissions(storedPermissions);
        setUserRoleId(roleId);
        setUserRoleName(roleName);
      }
    }
  }, [currentEmployee, getScreenMode]);

  // Function to reload permissions based on current mode
  const reloadPermissions = useCallback(() => {
    const mode = getScreenMode('timesheet-grid');
    if (mode === 'live') {
      // In live mode, re-fetch from API
      hasFetchedFromApi.current = false; // Reset flag to allow re-fetch
      fetchPermissionsFromApi();
    } else {
      // In demo mode, reload from localStorage
      reloadPermissionsFromStorage();
    }
  }, [getScreenMode, fetchPermissionsFromApi, reloadPermissionsFromStorage]);

  // Listen for localStorage changes (when permissions are saved in EditRolePage)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key?.startsWith('userRolePermissions_') || event.key === 'currentEditingRoleId') {
        reloadPermissionsFromStorage();
      }
    };

    // Also listen for custom event dispatched when saving permissions
    const handlePermissionsUpdated = () => {
      reloadPermissions(); // Use the mode-aware reload function
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('permissionsUpdated', handlePermissionsUpdated);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('permissionsUpdated', handlePermissionsUpdated);
    };
  }, [reloadPermissionsFromStorage, reloadPermissions]);

  // Load permissions from the current employee, API, or localStorage (demo mode)
  useEffect(() => {
    if (currentEmployee) {
      // Check if currentEmployee has permissions (from the updated API response in live mode)
      const empWithPermissions = currentEmployee as {
        userRoleId?: number;
        userRoleName?: string;
        permissions?: UserRolePermissions;
      };
      
      // Check the current mode first
      const mode = getScreenMode('timesheet-grid');
      
      if (empWithPermissions.permissions) {
        // Permissions available in currentEmployee (from BootstrapGate in production)
        setPermissions(empWithPermissions.permissions);
        setUserRoleId(empWithPermissions.userRoleId ?? null);
        setUserRoleName(empWithPermissions.userRoleName ?? null);
        setIsLoading(false);
      } else if (mode === 'live') {
        // Live mode but no permissions in currentEmployee - fetch from API
        // This handles the case where BootstrapGate didn't run (development mode)
        // or when currentEmployee was set from demo data
        if (!hasFetchedFromApi.current) {
          fetchPermissionsFromApi().finally(() => {
            setIsLoading(false);
          });
        } else {
          // Already fetched, just set loading to false
          setIsLoading(false);
        }
      } else {
        // Demo mode: try to load permissions from localStorage
        // First, check if employee has an assigned role in localStorage
        const assignedRoleId = getEmployeeRoleIdFromStorage(currentEmployee.id);
        if (assignedRoleId) {
          const storedPermissions = loadRolePermissionsFromStorage(assignedRoleId);
          const roleName = getRoleNameFromStorage(assignedRoleId);
          if (storedPermissions) {
            setPermissions(storedPermissions);
            setUserRoleId(assignedRoleId);
            setUserRoleName(roleName);
          } else {
            setPermissions(getDefaultPermissions());
            setUserRoleId(assignedRoleId);
            setUserRoleName(roleName);
          }
        } else {
          // No role assigned - check if there's a "current role being edited" in localStorage
          // This allows testing permissions while editing a role
          const currentEditingRoleId = localStorage.getItem('currentEditingRoleId');
          if (currentEditingRoleId) {
            const roleId = parseInt(currentEditingRoleId, 10);
            const storedPermissions = loadRolePermissionsFromStorage(roleId);
            const roleName = getRoleNameFromStorage(roleId);
            if (storedPermissions) {
              setPermissions(storedPermissions);
              setUserRoleId(roleId);
              setUserRoleName(roleName);
            } else {
              setPermissions(getDefaultPermissions());
              setUserRoleId(null);
              setUserRoleName(null);
            }
          } else {
            setPermissions(getDefaultPermissions());
            setUserRoleId(null);
            setUserRoleName(null);
          }
        }
        setIsLoading(false);
      }
    }
  }, [currentEmployee, getScreenMode, fetchPermissionsFromApi]);

  // Helper to check access level
  const checkAccessLevel = useCallback((accessLevel: AccessLevel | undefined, targetEmployeeId: number): boolean => {
    if (!currentEmployee) return false;
    
    const level = accessLevel ?? 'own';
    switch (level) {
      case 'all':
        return true;
      case 'team':
        // Check if target is self or a direct report
        return targetEmployeeId === currentEmployee.id || 
               currentEmployee.managerId === targetEmployeeId;
      case 'own':
        return targetEmployeeId === currentEmployee.id;
      case 'none':
        return false;
      default:
        return targetEmployeeId === currentEmployee.id;
    }
  }, [currentEmployee]);

  // Timesheet permissions
  const canViewTimesheet = useCallback((targetEmployeeId: number): boolean => {
    return checkAccessLevel(permissions?.timesheetAccess?.viewAccess, targetEmployeeId);
  }, [permissions, checkAccessLevel]);

  const canEditTimesheet = useCallback((targetEmployeeId: number): boolean => {
    return checkAccessLevel(permissions?.timesheetAccess?.editAccess, targetEmployeeId);
  }, [permissions, checkAccessLevel]);

  const canDeleteTimesheet = useCallback((): boolean => {
    return permissions?.timesheetAccess?.allowDelete ?? false;
  }, [permissions]);

  const canDuplicateTimesheet = useCallback((): boolean => {
    return permissions?.timesheetAccess?.allowDuplicate ?? false;
  }, [permissions]);

  const canRecallTimesheet = useCallback((): boolean => {
    return permissions?.timesheetAccess?.allowRecall ?? false;
  }, [permissions]);

  const getTimesheetViewAccessLevel = useCallback((): AccessLevel => {
    return permissions?.timesheetAccess?.viewAccess ?? 'own';
  }, [permissions]);

  const getTimesheetEditAccessLevel = useCallback((): AccessLevel => {
    return permissions?.timesheetAccess?.editAccess ?? 'own';
  }, [permissions]);

  // Personal homepage permission — uses Model B (flat string code array)
  const canViewPersonalHomepage = useCallback((): boolean => {
    return permissions?.permissions?.includes('dashboard.view_own') ?? true;
  }, [permissions]);

  // Approval permissions
  const canViewApprovalDashboard = useCallback((): boolean => {
    return permissions?.approvalPermissions?.viewDashboard ?? false;
  }, [permissions]);

  const canViewApprovals = useCallback((): boolean => {
    return permissions?.timesheetApprovalPermissions?.viewApprovals ?? false;
  }, [permissions]);

  const canApproveReject = useCallback((): boolean => {
    // Backend uses editTimesheets and rejectTimesheets instead of approveReject
    return (permissions?.timesheetApprovalPermissions?.editTimesheets ?? false) ||
           (permissions?.timesheetApprovalPermissions?.rejectTimesheets ?? false);
  }, [permissions]);

  const canEditTimesheetInApproval = useCallback((): boolean => {
    // Dedicated check for the "Edit Timesheets" approval permission
    // Intentionally separate from canApproveReject so it can be toggled independently
    return permissions?.timesheetApprovalPermissions?.editTimesheets ?? false;
  }, [permissions]);

  const canBulkApprove = useCallback((): boolean => {
    return permissions?.timesheetApprovalPermissions?.bulkApprove ?? false;
  }, [permissions]);

  const canDelegateApproval = useCallback((): boolean => {
    return permissions?.timesheetApprovalPermissions?.delegateApproval ?? false;
  }, [permissions]);

  // When compulsoryRejectionReason is ON, a minimum-length reason is required before submitting a rejection.
  // When OFF, the reason field is still shown but is optional — the submit button is enabled immediately.
  // Defaults to true so existing behaviour is preserved until the API confirms the flag value.
  const requiresRejectionReason = useCallback((): boolean => {
    return permissions?.timesheetApprovalPermissions?.compulsoryRejectionReason ?? true;
  }, [permissions]);

  /** Whether the approver can see the Policy Violations column, section in the issues sheet,
   *  and the policy violations portion of the flagged-approval confirmation dialog.
   *  Defaults to true so existing behaviour is preserved until the API confirms the flag. */
  const canViewPolicyViolations = useCallback((): boolean => {
    return permissions?.timesheetApprovalPermissions?.viewPolicyViolations ?? true;
  }, [permissions]);

  /** Whether the approver can see the Status/Anomaly column, the AnomalyIndicator badge,
   *  the Issues button in the view dialog, the AIInsightsSheet, and the anomaly portion of
   *  the flagged-approval confirmation dialog.
   *  Defaults to true so existing behaviour is preserved until the API confirms the flag. */
  const canViewDetectedIssues = useCallback((): boolean => {
    return permissions?.timesheetApprovalPermissions?.viewDetectedIssues ?? true;
  }, [permissions]);

  // Report permissions
  const canViewReport = useCallback((reportType: 'timesheet' | 'utilisation' | 'missingTimesheets'): boolean => {
    const reportPermissions = permissions?.reportAccessPermissions;
    if (!reportPermissions) return false;

    switch (reportType) {
      case 'timesheet':
        return reportPermissions.timesheetReport?.enabled ?? false;
      case 'utilisation':
        return reportPermissions.utilisationReport?.enabled ?? false;
      // 'billableHours' removed — belongs in the Projects module of the PSA suite.
      case 'missingTimesheets':
        return reportPermissions.missingTimesheetReport?.enabled ?? true;
      default:
        return false;
    }
  }, [permissions]);

  const canExportReport = useCallback((reportType: 'timesheet' | 'utilisation' | 'missingTimesheets'): boolean => {
    const reportPermissions = permissions?.reportAccessPermissions;
    if (!reportPermissions) return true;

    // Backend uses allowExport instead of exportAccess
    switch (reportType) {
      case 'timesheet':
        return reportPermissions.timesheetReport?.allowExport ?? false;
      case 'utilisation':
        return reportPermissions.utilisationReport?.allowExport ?? false;
      // 'billableHours' removed — belongs in the Projects module of the PSA suite.
      case 'missingTimesheets':
        return reportPermissions.missingTimesheetReport?.allowExport ?? true;
      default:
        return false;
    }
  }, [permissions]);

  const getReportViewAccessLevel = useCallback((reportType: 'timesheet' | 'utilisation' | 'missingTimesheets'): AccessLevel => {
    const reportPermissions = permissions?.reportAccessPermissions;
    if (!reportPermissions) return 'all';

    switch (reportType) {
      case 'timesheet':
        return reportPermissions.timesheetReport?.viewAccess ?? 'own';
      case 'utilisation':
        return reportPermissions.utilisationReport?.viewAccess ?? 'own';
      // 'billableHours' removed — belongs in the Projects module of the PSA suite.
      case 'missingTimesheets':
        return reportPermissions.missingTimesheetReport?.viewAccess ?? 'all';
      default:
        return 'own';
    }
  }, [permissions]);

  // System settings permissions
  const canManageRolesPermissions = useCallback((): boolean => {
    return permissions?.systemSettingsPermissions?.manageRolesPermissions ?? false;
  }, [permissions]);

  const canManageSystemSettings = useCallback((): boolean => {
    return permissions?.systemSettingsPermissions?.manageGeneralSettings === 'configure';
  }, [permissions]);

  const canViewAuditLogs = useCallback((): boolean => {
    return permissions?.systemSettingsPermissions?.viewAuditLogs ?? false;
  }, [permissions]);

  const canManageApprovalWorkflows = useCallback((): boolean => {
    // Field was previously misnamed manageApprovalWorkflows — API returns manageApprovalsSettings
    return permissions?.systemSettingsPermissions?.manageApprovalsSettings ?? false;
  }, [permissions]);

  const canManageNotifications = useCallback((): boolean => {
    return permissions?.systemSettingsPermissions?.manageNotifications ?? true;
  }, [permissions]);

  const canAccessAnySettings = useCallback((): boolean => {
    const settings = permissions?.systemSettingsPermissions;
    if (!settings) return false;
    return (
      (settings.manageGeneralSettings !== 'none') ||
      settings.manageRolesPermissions ||
      settings.manageApprovalsSettings ||
      (settings.manageNotifications ?? true) ||
      settings.viewAuditLogs
    );
  }, [permissions]);

  // Settings tab sub-permissions — each gates one specific Settings tab
  // Requires manageGeneralSettings === 'configure' as master switch + the individual sub-permission
  const canManageDeadlines = useCallback((): boolean => {
    const s = permissions?.systemSettingsPermissions;
    return s?.manageGeneralSettings === 'configure' && (s?.manageDeadlines ?? true);
  }, [permissions]);

  const canManagePolicySettings = useCallback((): boolean => {
    const s = permissions?.systemSettingsPermissions;
    return s?.manageGeneralSettings === 'configure' && (s?.managePolicySettings ?? true);
  }, [permissions]);

  const canManageUtilisationSettings = useCallback((): boolean => {
    const s = permissions?.systemSettingsPermissions;
    return s?.manageGeneralSettings === 'configure' && (s?.manageUtilisationSettings ?? true);
  }, [permissions]);

  // ⚠️ manageTimeEntrySettings and manageWorkTypeSettings are new fields — backend support required
  // Default to true (same as configure access) until the field is in the API response
  const canManageTimeEntrySettings = useCallback((): boolean => {
    const s = permissions?.systemSettingsPermissions;
    return s?.manageGeneralSettings === 'configure' && (s?.manageTimeEntrySettings ?? true);
  }, [permissions]);

  const canManageWorkTypeSettings = useCallback((): boolean => {
    const s = permissions?.systemSettingsPermissions;
    return s?.manageGeneralSettings === 'configure' && (s?.manageWorkTypeSettings ?? true);
  }, [permissions]);



  // Team/Employee directory permissions
  const canViewEmployeeDirectory = useCallback((): boolean => {
    // viewDirectory is a boolean from the API (not AccessLevel) — true = directory visible
    return permissions?.employeeDirectoryPermissions?.viewDirectory ?? true;
  }, [permissions]);

  const canViewEmployeeDetails = useCallback((): boolean => {
    return permissions?.employeeDirectoryPermissions?.viewDetails ?? true;
  }, [permissions]);

  // Integrations permissions
  const canViewIntegrations = useCallback((): boolean => {
    return permissions?.integrationsPermissions?.viewIntegrations ?? true;
  }, [permissions]);

  const canManageIntegrations = useCallback((): boolean => {
    return permissions?.integrationsPermissions?.manageIntegrations ?? true;
  }, [permissions]);

  // Company management permissions
  const canViewCompanies = useCallback((): boolean => {
    return permissions?.companyManagementPermissions?.viewCompanies ?? false;
  }, [permissions]);

  // Check if user can access any report type
  const canAccessAnyReports = useCallback((): boolean => {
    const rp = permissions?.reportAccessPermissions;
    if (!rp) return false;
    return (
      (rp.timesheetReport?.enabled ?? false) ||
      (rp.missingTimesheetReport?.enabled ?? false) ||
      (rp.utilisationReport?.enabled ?? false)
      // billableHoursReport removed — belongs in the Projects module of the PSA suite.
    );
  }, [permissions]);

  // Smart features permissions — two-tier model:
  // Both the org-level setting AND the role-level permission must be true for a feature to show.
  // The org setting acts as a master switch; the role permission allows per-profile control on top.
  // orgEnabled && roleEnabled → visible
  // orgEnabled && !roleEnabled → hidden (role blocks it)
  // !orgEnabled → hidden for everyone regardless of role

  // NOTE: These functions accept an optional orgEnabled boolean (from organizationSettings).
  // The consuming component is responsible for passing the org-level flag.
  // This keeps the permission context decoupled from the settings context.

  /** @deprecated Use canCopyFromLastWeek() instead */
  const canUseAiAssistant = useCallback((): boolean => {
    return permissions?.smartFeaturesPermissions?.enableAiInsights ?? true;
  }, [permissions]);

  /** @deprecated Use canUseSmartFill() instead */
  const canUseAnomalyDetection = useCallback((): boolean => {
    return permissions?.smartFeaturesPermissions?.enableAutoSmartFill ?? true;
  }, [permissions]);



  // ── Correctly-named smart feature permission functions ──────────────────────

  /** Copy Last Week button — reads enableCopyFromLastWeek role permission. */
  const canCopyFromLastWeek = useCallback((): boolean => {
    return permissions?.smartFeaturesPermissions?.enableCopyFromLastWeek ?? true;
  }, [permissions]);

  /** Auto Smart Fill button — reads enableAutoSmartFill role permission. */
  const canUseSmartFill = useCallback((): boolean => {
    return permissions?.smartFeaturesPermissions?.enableAutoSmartFill ?? true;
  }, [permissions]);

  /** AI Insights button — reads enableAiInsights role permission. */
  const canUseAiInsights = useCallback((): boolean => {
    return permissions?.smartFeaturesPermissions?.enableAiInsights ?? true;
  }, [permissions]);

  const value: PermissionContextType = {
    permissions,
    userRoleId,
    userRoleName,
    isLoading,
    canViewTimesheet,
    canEditTimesheet,
    canDeleteTimesheet,
    canDuplicateTimesheet,
    canRecallTimesheet,
    getTimesheetViewAccessLevel,
    getTimesheetEditAccessLevel,
    canViewPersonalHomepage,
    canViewApprovalDashboard,
    canViewApprovals,
    canApproveReject,
    canEditTimesheetInApproval,
    canBulkApprove,
    canDelegateApproval,
    requiresRejectionReason,
    canViewPolicyViolations,
    canViewDetectedIssues,
    canViewReport,
    canExportReport,
    getReportViewAccessLevel,
    canManageRolesPermissions,
    canManageSystemSettings,
    canViewAuditLogs,
    canManageApprovalWorkflows,
    canManageNotifications,
    canAccessAnySettings,
    canManageDeadlines,
    canManagePolicySettings,
    canManageUtilisationSettings,
    canManageTimeEntrySettings,
    canManageWorkTypeSettings,
    canViewEmployeeDirectory,
    canViewEmployeeDetails,
    canViewIntegrations,
    canManageIntegrations,
    canViewCompanies,
    canAccessAnyReports,
    canUseAiAssistant,
    canUseAnomalyDetection,
    canCopyFromLastWeek,
    canUseSmartFill,
    canUseAiInsights,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// Default permissions for users without a role assigned
function getDefaultPermissions(): UserRolePermissions {
  return {
    timesheetAccess: {
      viewAccess: 'own',
      editAccess: 'own',
      allowDelete: false,
    },
    smartFeaturesPermissions: {
      aiAssistant: true,
      anomalyDetection: true,
    },
    approvalPermissions: {
      viewDashboard: false,
      viewTeamMetrics: false,
      viewAllMetrics: false,
    },
    timesheetApprovalPermissions: {
      viewApprovals: true,
      editTimesheets: true,
      rejectTimesheets: true,
      bulkApprove: true,
      delegateApproval: true, // Managers can delegate approvals by default
    },
    reportAccessPermissions: {
      timesheetReport: { enabled: true, viewAccess: 'all', allowExport: true },
      utilisationReport: { enabled: false, viewAccess: 'none', allowExport: false },
      // billableHoursReport removed — belongs in the Projects module of the PSA suite.
    },
    employeeDirectoryPermissions: {
      viewDirectory: true,
      viewDetails: true,
      viewContactInfo: false,
      viewWorkingPatterns: false,
    },
    integrationsPermissions: {
      viewIntegrations: true,
      manageIntegrations: true,
    },
    companyManagementPermissions: {
      viewCompanies: false,
      manageCompanies: false,
    },
    systemSettingsPermissions: {
      manageGeneralSettings: 'configure', // Changed from 'none' to 'configure' to show Settings in demo mode
      manageApprovalsSettings: true,
      manageNotifications: true,
      manageRolesPermissions: true,
      viewAuditLogs: true,
    },
  };
}
